import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  signupSchema,
  type SignupInput,
} from "../../domain/validation/signupSchema";
import { useSignup } from "../../application/hooks/useSignup";
import { useOnboardingData } from "../../application/hooks/useOnboardingData";
import { useRegistrationEligibility } from "../../application/hooks/useRegistrationEligibility";
import { MenuItem } from "@mui/material";
import AuthPrimaryButton from "./AuthPrimaryButton";
import AuthTextField from "./AuthTextField";
import { isValidPhoneNumber, type CountryCode } from "libphonenumber-js";
import PhoneInput from "react-phone-number-input";
import { Link, useNavigate } from "react-router-dom";
import type { Country } from "../../domain/entities/Countries";
import type { States } from "../../domain/entities/States";
import type { Cities } from "../../domain/entities/Cities";
import type { Designations } from "../../domain/entities/Designations";
import type {
  AgreementDetails,
  Agreement as AgreementEntity,
  RegistrationAgreementClause,
  RegistrationScopeFilters,
} from "../../domain/entities/Agreement";
import Agreement from "./Agreement";
import {
  InstitutionType,
  getInstitutionTypeLabel,
  SignupPurpose,
  getSignupPurposeLabel,
} from "../../domain/constants/signupEnums";
import {
  DEFAULT_PHONE_COUNTRY,
  resolvePhoneCountryCode,
} from "../../domain/constants/phoneCountryDefaults";
import { authTextButtonStyle } from "../theme/authTextButtonStyle";
import { persistRegistrationAgreementDownload } from "../../application/hooks/useDownloadAgreement";

const SignupForm = () => {
  const { t } = useTranslation("auth");
  const { signup, loading: signupLoading, error: signupError } = useSignup();
  const {
    checkRegistrationEligibility,
    loading: eligibilityLoading,
    error: eligibilityError,
  } = useRegistrationEligibility();
  const {
    fetchCountries,
    fetchStates,
    fetchCities,
    fetchDesignations,
    fetchAgreements,
    fetchRequiredRegistrationClauses,
  } = useOnboardingData();

  const navigate = useNavigate();

  const [form, setForm] = useState<SignupInput>({
    name: "",
    institute_type: "",
    institute_name: "",
    institute_country_id: "",
    institute_state_id: "",
    institute_city_id: "",
    instutue_pincode: "",
    designation_id: "",
    phone: "",
    email: "",
    purpose: "",
  });

  const [errors, setErrors] = useState<Partial<SignupInput>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);

  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<States[]>([]);
  const [cities, setCities] = useState<Cities[]>([]);
  const [designations, setDesignations] = useState<Designations[]>([]);
  const [agreements, setAgreements] = useState<AgreementEntity[]>([]);
  const [agreementDetails, setAgreementDetails] = useState<AgreementDetails[]>(
    [],
  );
  const [agreementClauses, setAgreementClauses] = useState<
    RegistrationAgreementClause[]
  >([]);
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>(
    DEFAULT_PHONE_COUNTRY,
  );
  const [pendingSignup, setPendingSignup] = useState<SignupInput | null>(null);
  const [agreementSubmitError, setAgreementSubmitError] = useState<
    string | null
  >(null);
  const emailCheckRef = useRef(0);
  const phoneCheckRef = useRef(0);

  useEffect(() => {
    const loadInitialDetails = async () => {
      fetchCountries().then(setCountries).catch(console.error);

      fetchDesignations().then(setDesignations).catch(console.error);

    };

    loadInitialDetails();
  }, [fetchCountries, fetchDesignations]);

  useEffect(() => {
    if (!form.institute_country_id) {
      return;
    }

    fetchStates(form.institute_country_id).then(setStates).catch(console.error);
  }, [form.institute_country_id, fetchStates]);

  useEffect(() => {
    if (!form.institute_state_id) {
      return;
    }

    fetchCities(form.institute_state_id).then(setCities).catch(console.error);
  }, [form.institute_state_id, fetchCities]);

  useEffect(() => {
    const email = form.email.trim();
    const requestId = emailCheckRef.current + 1;
    emailCheckRef.current = requestId;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        const eligibility = await checkRegistrationEligibility({ email });
        if (emailCheckRef.current !== requestId) {
          return;
        }

        setErrors((prev) => ({
          ...prev,
          email: eligibility.exists
            ? eligibility.message || t("signup.emailAlreadyRegistered")
            : undefined,
        }));
      } catch (error) {
        if (emailCheckRef.current !== requestId) {
          return;
        }

        setErrors((prev) => ({
          ...prev,
          email:
            error instanceof Error
              ? error.message
              : t("signup.emailValidationError"),
        }));
      }
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [form.email, checkRegistrationEligibility]);

  useEffect(() => {
    const phone = form.phone;
    const requestId = phoneCheckRef.current + 1;
    phoneCheckRef.current = requestId;

    if (!phone || !isValidPhoneNumber(phone)) {
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        const eligibility = await checkRegistrationEligibility({ phone });
        if (phoneCheckRef.current !== requestId) {
          return;
        }

        setErrors((prev) => ({
          ...prev,
          phone: eligibility.exists
            ? eligibility.message || t("signup.phoneAlreadyRegistered")
            : undefined,
        }));
      } catch (error) {
        if (phoneCheckRef.current !== requestId) {
          return;
        }

        setErrors((prev) => ({
          ...prev,
          phone:
            error instanceof Error
              ? error.message
              : t("signup.phoneValidationError"),
        }));
      }
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [form.phone, checkRegistrationEligibility]);

  const getFormWithCityPincode = (input: SignupInput): SignupInput => {
    const selectedCity = cities.find((c) => c.id === input.institute_city_id);

    return {
      ...input,
      instutue_pincode: selectedCity?.pincode || "",
    };
  };

  const getValidationErrors = (input: SignupInput): Partial<SignupInput> => {
    const result = signupSchema.safeParse(getFormWithCityPincode(input));

    if (result.success) {
      return {};
    }

    const fieldErrors: Partial<SignupInput> = {};
    result.error.issues.forEach((err) => {
      const field = err.path[0] as keyof SignupInput;
      fieldErrors[field] = t(err.message);
    });

    return fieldErrors;
  };

  const updateValidationErrorsForFields = (
    input: SignupInput,
    fields: (keyof SignupInput)[],
  ) => {
    const validationErrors = getValidationErrors(input);

    setErrors((prev) => {
      const next = { ...prev };

      fields.forEach((field) => {
        next[field] = validationErrors[field];
      });

      return next;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value: rawValue } = e.target;
    const value =
      name === "name" ? rawValue.replace(/[^a-zA-Z\s_-]/g, "") : rawValue;
    const field = name as keyof SignupInput;

    if (name === "institute_country_id") {
      const selected = countries.find((c) => c.id === value);
      setPhoneCountry(resolvePhoneCountryCode(selected?.code, selected?.name));
      setStates([]);
      setCities([]);
    }

    if (name === "institute_state_id") {
      setCities([]);
    }

    const updatedForm = {
      ...form,
      [name]: value,
      ...(name === "institute_country_id"
        ? { institute_state_id: "", institute_city_id: "" }
        : {}),
      ...(name === "institute_state_id" ? { institute_city_id: "" } : {}),
    };

    setForm(updatedForm);

    if (hasSubmitted) {
      updateValidationErrorsForFields(updatedForm, [
        field,
        ...(name === "institute_country_id"
          ? (["institute_state_id", "institute_city_id"] as const)
          : []),
        ...(name === "institute_state_id"
          ? (["institute_city_id"] as const)
          : []),
      ]);
    } else if (name === "email") {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handleContinue = async (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    setHasSubmitted(true);
    setAgreementSubmitError(null);

    const updatedForm = getFormWithCityPincode(form);

    const result = signupSchema.safeParse(updatedForm);

    if (!result.success) {
      const fieldErrors: Partial<SignupInput> = {};

      result.error.issues.forEach((err) => {
        const field = err.path[0] as keyof SignupInput;
        fieldErrors[field] = t(err.message);
      });

      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    try {
      const [emailEligibility, phoneEligibility] = await Promise.all([
        checkRegistrationEligibility({ email: result.data.email.trim() }),
        checkRegistrationEligibility({ phone: result.data.phone }),
      ]);

      const eligibilityErrors: Partial<SignupInput> = {};

      if (emailEligibility.exists) {
        eligibilityErrors.email =
          emailEligibility.message || t("signup.emailAlreadyRegistered");
      }

      if (phoneEligibility.exists) {
        eligibilityErrors.phone =
          phoneEligibility.message || t("signup.phoneAlreadyRegistered");
      }

      if (Object.keys(eligibilityErrors).length > 0) {
        setErrors(eligibilityErrors);
        return;
      }

      const agreementPurpose =
        result.data.purpose === SignupPurpose.DEMO_REQUEST
          ? SignupPurpose.DEMO_REQUEST
          : undefined;
      const registrationScopeFilters: RegistrationScopeFilters = {
        institute_type: result.data.institute_type,
        country_id: result.data.institute_country_id,
        purpose: agreementPurpose,
      };
      const [agreementsData, clauses] = await Promise.all([
        fetchAgreements(registrationScopeFilters),
        fetchRequiredRegistrationClauses(registrationScopeFilters),
      ]);

      if (agreementsData.agreements.length === 0) {
        setAgreementSubmitError(t("signup.agreementsLoadError"));
        return;
      }

      if (clauses.length === 0) {
        setAgreementSubmitError(t("signup.clausesLoadError"));
        return;
      }

      setForm(updatedForm);
      setPendingSignup(result.data);
      setAgreements(agreementsData.agreements);
      setAgreementDetails(agreementsData.details);
      setAgreementClauses(clauses);
      setAgreementSubmitError(null);
      setShowAgreement(true);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("signup.registrationValidationError");
      setErrors({
        email: message,
      });
    }
  };

  const handleAgreementRegister = async () => {
    setAgreementSubmitError(null);

    if (!pendingSignup) {
      setShowAgreement(false);
      setErrors({
        name: t("signup.sessionExpired"),
      });
      return;
    }

    if (agreements.length === 0) {
      setAgreementSubmitError(t("signup.agreementsLoadErrorRefresh"));
      return;
    }

    if (agreementClauses.length === 0) {
      setAgreementSubmitError(t("signup.clausesLoadErrorGoBack"));
      return;
    }

    let response: Awaited<ReturnType<typeof signup>>;
    try {
      response = await signup({
        ...pendingSignup,
        institute_code: "125",
        accepted_agreement_ids: agreements.map((a) => a.id),
        accepted_clause_ids: agreementClauses.map((clause) => clause.id),
      });
    } catch {
      // Error message is surfaced via signupError from useSignup
      return;
    }

    // Registration has succeeded from here on: the agreement PDF link is
    // optional bookkeeping and must never keep the user on this dialog.
    try {
      if (response?.agreement_pdf?.token) {
        persistRegistrationAgreementDownload({
          requestId: response.request_id,
          token: response.agreement_pdf.token,
          downloadPath: response.agreement_pdf.download_path,
          expiresAt: response.agreement_pdf.expires_at,
        });
      }
    } catch (err) {
      console.error("Failed to store registration agreement download:", err);
    }

    setShowAgreement(false);
    setPendingSignup(null);
    navigate("/register-success");
  };

  const phoneInputKey = form.institute_country_id || DEFAULT_PHONE_COUNTRY;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <form
        className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="auth-registration-scrollbar flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-5 pr-3 pt-1 sm:gap-5 sm:pr-4">
          <AuthTextField
            name="name"
            label={t("signup.nameLabel")}
            value={form.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />

          <AuthTextField
            select
            name="institute_type"
            label={t("signup.instituteTypeLabel")}
            value={form.institute_type}
            onChange={handleChange}
            error={!!errors.institute_type}
            helperText={errors.institute_type}
            fullWidth
          >
            {(Object.values(InstitutionType) as InstitutionType[]).map(
              (value) => (
                <MenuItem key={value} value={value}>
                  {getInstitutionTypeLabel(value, t)}
                </MenuItem>
              ),
            )}
          </AuthTextField>

          <AuthTextField
            name="institute_name"
            label={t("signup.instituteNameLabel")}
            value={form.institute_name}
            onChange={handleChange}
            error={!!errors.institute_name}
            helperText={errors.institute_name}
            fullWidth
          />

          <AuthTextField
            select
            name="institute_country_id"
            label={t("signup.countryLabel")}
            value={form.institute_country_id}
            onChange={handleChange}
            error={!!errors.institute_country_id}
            helperText={errors.institute_country_id}
            fullWidth
          >
            {countries.length === 0 ? (
              <MenuItem disabled>{t("signup.loadingCountries")}</MenuItem>
            ) : (
              countries.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))
            )}
          </AuthTextField>

          <AuthTextField
            select
            name="institute_state_id"
            label={t("signup.stateLabel")}
            value={form.institute_state_id}
            onChange={handleChange}
            error={!!errors.institute_state_id}
            helperText={errors.institute_state_id}
            fullWidth
          >
            {states.length === 0 ? (
              <MenuItem disabled>{t("signup.selectCountryFirst")}</MenuItem>
            ) : (
              states.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))
            )}
          </AuthTextField>

          <AuthTextField
            select
            name="institute_city_id"
            label={t("signup.cityLabel")}
            value={form.institute_city_id}
            onChange={handleChange}
            error={!!errors.institute_city_id}
            helperText={errors.institute_city_id}
            fullWidth
          >
            {cities.length === 0 ? (
              <MenuItem disabled>{t("signup.selectStateFirst")}</MenuItem>
            ) : (
              cities.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))
            )}
          </AuthTextField>

          <AuthTextField
            select
            name="designation_id"
            label={t("signup.designationLabel")}
            value={form.designation_id}
            onChange={handleChange}
            error={!!errors.designation_id}
            helperText={errors.designation_id}
            fullWidth
          >
            {designations.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name}
              </MenuItem>
            ))}
          </AuthTextField>

          <div className="flex flex-col gap-1">
            <div
              className={`flex h-12 overflow-hidden rounded-lg border bg-transparent transition-colors focus-within:border-ring ${
                errors.phone ? "border-destructive" : "border-input"
              }`}
            >
              <PhoneInput
                key={phoneInputKey}
                defaultCountry={phoneCountry}
                international
                countryCallingCodeEditable={false}
                placeholder={t("signup.phonePlaceholder")}
                value={form.phone}
                onCountryChange={(country) => {
                  if (country) {
                    setPhoneCountry(country);
                  }
                }}
                onChange={(val) => {
                  const updatedForm = {
                    ...form,
                    phone: val ?? "",
                  };

                  setForm(updatedForm);

                  if (hasSubmitted) {
                    updateValidationErrorsForFields(updatedForm, ["phone"]);
                  } else {
                    setErrors((prev) => ({ ...prev, phone: undefined }));
                  }
                }}
              />
            </div>
            {errors.phone && (
              <p className="mx-3 text-xs text-destructive">{errors.phone}</p>
            )}
          </div>

          <AuthTextField
            name="email"
            label={t("signup.emailLabel")}
            value={form.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
          />

          <AuthTextField
            select
            name="purpose"
            label={t("signup.purposeLabel")}
            value={form.purpose}
            onChange={handleChange}
            error={!!errors.purpose}
            helperText={errors.purpose}
            fullWidth
          >
            {(Object.values(SignupPurpose) as SignupPurpose[]).map((value) => (
              <MenuItem key={value} value={value}>
                {getSignupPurposeLabel(value, t)}
              </MenuItem>
            ))}
          </AuthTextField>
        </div>

        {/* FIXED BUTTON */}
        <div className="sticky bottom-0 z-10 shrink-0 border-t border-gray-100 bg-white pt-4">
          <div className="flex flex-col gap-4">
            <AuthPrimaryButton
              type="button"
              onClick={handleContinue}
              loading={eligibilityLoading}
            >
              {t("signup.continue")}
            </AuthPrimaryButton>

            {eligibilityError && (
              <p className="text-center text-sm text-red-500">
                {eligibilityError}
              </p>
            )}

            {agreementSubmitError && !showAgreement && (
              <p className="text-center text-sm text-red-500">
                {agreementSubmitError}
              </p>
            )}

            <div className="text-center text-sm leading-[var(--type-button-line-height)] text-gray-600">
              {t("signup.haveAccount")}{" "}
              <Link
                to="/login"
                className="text-primary"
                style={authTextButtonStyle}
              >
                {t("signup.loginNow")}
              </Link>
            </div>
          </div>
        </div>
      </form>

      <Agreement
        open={showAgreement}
        onClose={() => {
          if (!signupLoading) {
            setAgreementSubmitError(null);
            setShowAgreement(false);
          }
        }}
        onAgree={handleAgreementRegister}
        agreementDetails={agreementDetails}
        acceptanceClauses={agreementClauses}
        expectedName={pendingSignup?.name ?? form.name}
        loading={signupLoading}
        error={agreementSubmitError ?? signupError}
      />
    </div>
  );
};

export default SignupForm;
